import type { Invoice } from '../types/subscriptionManagement'
import {
  isPaidInvoiceStatus,
  parseYmdLocal,
} from './subscriptionInvoiceCoverage'

/**
 * Split a paid invoice amount (INR) across calendar months.
 * Uses coverage [subscriptionStartDate, subscriptionEndDate) when both are set;
 * subscriptionEndDate is treated as an exclusive renewal boundary.
 * Legacy rows without coverage → full amount in invoice date month.
 */
export function allocatePaidInvoiceInrToMonths(
  inv: Invoice,
  inrAmount: number,
): Record<string, number> {
  if (!Number.isFinite(inrAmount) || inrAmount === 0) return {}

  const start = parseYmdLocal(inv.subscriptionStartDate)
  const endExclusive = parseYmdLocal(inv.subscriptionEndDate)
  if (start && endExclusive && endExclusive.getTime() > start.getTime()) {
    return allocateInrByDayRange(start, endExclusive, inrAmount)
  }

  const ym = inv.invoiceDate?.slice(0, 7)
  if (ym) return { [ym]: inrAmount }
  return {}
}

function allocateInrByDayRange(
  start: Date,
  endExclusive: Date,
  totalInr: number,
): Record<string, number> {
  const dayCounts: Record<string, number> = {}
  const d = new Date(start.getFullYear(), start.getMonth(), start.getDate())

  while (d < endExclusive) {
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    dayCounts[ym] = (dayCounts[ym] || 0) + 1
    d.setDate(d.getDate() + 1)
  }

  const totalDays = Object.values(dayCounts).reduce((sum, n) => sum + n, 0)
  if (totalDays <= 0) return {}

  const out: Record<string, number> = {}
  for (const [ym, days] of Object.entries(dayCounts)) {
    out[ym] = (totalInr * days) / totalDays
  }
  return out
}

/** True if invoice should contribute to paid expense analytics / report. */
export function isPaidExpenseInvoice(inv: Invoice): boolean {
  return isPaidInvoiceStatus(inv.paymentStatus)
}

/**
 * Sum allocated INR for one invoice, optionally limited to allowed YYYY-MM months.
 */
export function allocatedInrForInvoice(
  inv: Invoice,
  inrAmount: number,
  allowedMonths: Set<string> | null,
): number {
  const buckets = allocatePaidInvoiceInrToMonths(inv, inrAmount)
  let sum = 0
  for (const [ym, portion] of Object.entries(buckets)) {
    if (allowedMonths && !allowedMonths.has(ym)) continue
    sum += portion
  }
  return sum
}

/** Month keys (YYYY-MM) where a paid invoice contributes expense in scope. */
export function paidInvoiceMonthKeys(inv: Invoice): string[] {
  if (!isPaidExpenseInvoice(inv)) return []
  return Object.keys(allocatePaidInvoiceInrToMonths(inv, 1))
}

export function hasPaidExpenseInMonths(
  invoices: Invoice[],
  allowedMonths: Set<string> | null,
): boolean {
  return invoices.some(inv => {
    const keys = paidInvoiceMonthKeys(inv)
    if (keys.length === 0) return false
    if (!allowedMonths) return true
    return keys.some(ym => allowedMonths.has(ym))
  })
}
